import type { TypeCmsHomepage, TypeCmsImage, TypeCmsProductDetail, TypeCmsProductsByCategoryList, TypeCmsProjectCategory, TypeCmsProjectDetail, TypeLang } from '@/schema'

const LOCALE = process.env.NEXT_PUBLIC_LOCALE
const CLIENT_NAME = process.env.NEXT_PUBLIC_CLIENT_NAME
const HOSTNAME = process.env.NEXT_PUBLIC_PROJECT_HOSTNAME

type TypeJsonLDProps =
  | { type: 'homepage'; data: TypeCmsHomepage; lang?: TypeLang }
  | { type: 'projects'; data: TypeCmsProjectCategory; lang?: TypeLang }
  | { type: 'products'; data: TypeCmsProductsByCategoryList; lang?: TypeLang }
  | { type: 'product'; data: TypeCmsProductDetail; lang?: TypeLang }
  | { type: 'project'; data: TypeCmsProjectDetail; lang?: TypeLang }

const VARIANTS_IN_PROJECT = [
  'grid_high2x', 'grid_high', 'grid_low2x',
  'pdp_high2x', 'pdp_high', 'pdp_low2x',
  'configurator_high2x', 'configurator_high', 'configurator_low2x',
  'zoom_high2x', 'zoom_high', 'zoom_low2x',
  'related_high2x', 'related_high', 'related_low2x',
  'slide_high2x', 'slide_high', 'slide_low2x',
  'cols_1_landscape_high2x', 'cols_1_landscape_high', 'cols_1_landscape_low2x',
  'cols_1_portrait_high2x', 'cols_1_portrait_high', 'cols_1_portrait_low2x',
  'cols_2_landscape_high2x', 'cols_2_landscape_high', 'cols_2_landscape_low2x',
  'cols_2_portrait_high2x', 'cols_2_portrait_high', 'cols_2_portrait_low2x',
  'full_width_high2x', 'full_width_high', 'full_width_low2x',
  'high2x', 'high', 'low2x',
  'full_high2x', 'full_high', 'full_low2x',
  'half_high2x', 'half_high', 'half_low2x',
  'thumb_high2x', 'thumb_high', 'thumb_low2x',
  'small_high2x', 'small_high', 'small_low2x',
  'menu_high2x', 'menu_high', 'menu_low2x',
  'xl', 'l', 'm', 's',
  'sliderXl', 'sliderL', 'sliderM', 'sliderS'
]

type TypeVariantsKeys = typeof VARIANTS_IN_PROJECT[number]

const getMediaVariant = (obj: Record<string, unknown>, variantNames: TypeVariantsKeys[] = VARIANTS_IN_PROJECT): unknown => {
  for (const key of variantNames) {
    if (key in obj) return obj[key]
  }
  return undefined // Return undefined if no matching key is found
}

const baseUrl = `https://${HOSTNAME}`
const organization = {
  '@type': 'Organization',
  name: CLIENT_NAME,
  url: baseUrl,
  image: 'https://nexia.es/opengraph-image.jpg',
  logo: {
    '@type': 'ImageObject',
    url: `${baseUrl}/logo.svg`, // Replace with your actual logo
  },
  email: 'info@nexia.es',
  telephone: '+34 93 715 47 12',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Carrer del Priorat, 14-20',
    addressLocality: 'Castellar del Vallès, Barcelona',
    addressCountry: 'ES',
    postalCode: '08211',
  },
  sameAs: [
    'https://www.instagram.com/nexialighting/',
    'https://es.linkedin.com/company/nexialighting',
    'https://www.pinterest.es/nexialighting/',
    'https://wa.me/0034676081574',
    'https://es-es.facebook.com/NexiaLighting/'
  ]
}

export function jsonLD({ data, type, lang = LOCALE as TypeLang }: TypeJsonLDProps) {
  if (type === 'homepage') {
    const itemList = data.projects?.map((project, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: project.name,
      url: `${baseUrl}/${project.slug[lang] ?? ''}` // Default to English, adapt as needed
    })) ?? []

    const mainEntity = {
      '@type': 'CollectionPage',
      name: `${CLIENT_NAME} Project Collection`,
      itemList
    }

    return {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: data.name,
      url: baseUrl,
      description: data.header?.subtitle ?? data.header?.title ?? 'Product catalog site',
      ...data.header?.medium?.image && { image: getImage(data.header.medium.image) },
      mainEntity,
      publisher: organization,
    }
  }

  if (type === 'projects') {
    return {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: data.name,
      url: `${baseUrl}/${data.slug[lang]}`,
      ...data.header?.medium?.image && { image: getImage(data.header.medium.image) },
      description: data.header.title,
      hasPart: data.projects.map(project => ({
        '@type': 'CreativeWork',
        name: project.name,
        url: `${baseUrl}/${project.slug[lang]}`
      })),
      publisher: organization,
    }
  }

  if (type === 'products') {
    return {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: data.name,
      url: `${baseUrl}/${data.slug[lang]}`,
      description: data.name,
      hasPart: data.products.map(product => ({
        '@type': 'Thing',
        name: product.name,
        model: product.model,
        url: `${baseUrl}/${product.slug[lang]}`,
        offers: {
          '@type': 'Offer',
          availability: 'https://schema.org/InStock'
        }
      })),
      publisher: organization
    }
  }

  if (type === 'product') {
    return {
      '@context': 'https://schema.org',
      publisher: organization,
      '@type': 'Product',
      name: data.name,
      description: data.description,
      sku: `${data.id}`,
      url: `${baseUrl}/${data.slug[lang]}`,
      image: data.media.map(m => {
        return getImage(m?.image)
      }),
      brand: {
        '@type': 'Brand',
        name: CLIENT_NAME
      },
      offers: {
        '@type': 'Offer',
        price: 0,
        priceCurrency: 'EUR',
        availability: 'https://schema.org/InStock',
        url: `${baseUrl}/${data.slug[lang]}`
      },
      ...data.colors && { color: data.colors.map(c => c.name) },
      isAccessoryOrSparePartFor: data.accessories?.map(acc => ({
        '@type': 'Product',
        name: acc.name,
        model: acc.model,
        url: `${baseUrl}/${acc.slug[lang]}`,
        ...acc.medium?.image && { image: getImage(acc.medium.image) }
      })),
      isRelatedTo: data.related_products?.map(p => ({
        '@type': 'Product',
        name: p.name,
        url: `${baseUrl}/${p.slug[lang]}`
      })),
      isConsumableFor: data.projects?.map(project => ({
        '@type': 'CreativeWork',
        name: project.name,
        url: `${baseUrl}/${project.slug[lang]}`
      })),
      additionalProperty: data.specifications.map(spec => ({
        '@type': 'PropertyValue',
        name: spec.type,
        value: spec.values.filter(v => v.type === 'text').map(v => (v as { value: string }).value).join(', ')
      }))
    }
  }


  if (type === 'project') {
    return {
      '@context': 'https://schema.org',
      '@type': 'CreativeWork',
      name: data.name,
      url: `${baseUrl}/projects/${data.slug?.slug?.en}`,
      description: data.header?.title,
      ...data.header?.medium?.image && { image: getImage(data.header.medium.image) },
      about: data.related_products?.map((product: TypeCmsProjectDetail['related_products'][0]) => ({
        '@type': 'Product',
        name: product.name,
        url: `${baseUrl}/products/${product.slug?.en}`,
        ...product.medium?.image && { image: getImage(product.medium.image) }
      }))
    }
  }

  return null
}

function getImage(data: TypeCmsImage): string {
  const variant = getMediaVariant(data, VARIANTS_IN_PROJECT)
  return (variant as { src: string })?.src || ''
}
